import Seo from "@/components/Seo";

/**
 * NOTE FOR DEVELOPERS:
 * Legal pages are starter content only and should be reviewed by a qualified
 * legal professional before launch.
 */
export default function CookiePolicy() {
    return (
        <div data-testid="page-cookie-policy">
            <Seo
                title="Cookie Policy | Chief AI Partners"
                description="How Chief AI Partners uses cookies and similar technologies, and how you can manage your preferences under the Privacy Act 1988 (Cth) and GDPR."
                path="/cookie-policy"
            />

            <section className="cap-section pt-24 md:pt-32 pb-12">
                <div className="cap-container">
                    <div className="cap-eyebrow mb-6">Legal</div>
                    <h1 className="font-serif text-navy text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
                        Cookie Policy
                    </h1>
                    <p className="text-slate text-sm mt-4">Last updated: April 2026</p>
                </div>
            </section>

            <section className="pb-24">
                <div className="cap-container max-w-3xl text-slate leading-relaxed space-y-10">
                    <p className="text-base md:text-lg">
                        This Cookie Policy explains how Chief AI Partners (a business operated by Sanlux Pty Ltd) uses cookies and similar technologies on chiefai.partners.
                    </p>
                    <p className="text-base md:text-lg">
                        It also explains your rights in relation to cookies under applicable laws, including the <em>Privacy Act 1988</em> (Cth) and, where applicable, the General Data Protection Regulation (GDPR).
                    </p>

                    <LegalBlock title="What are cookies">
                        <p>Cookies are small text files stored on your device when you visit a website. They help the website function, improve performance and provide insights into how the site is used.</p>
                    </LegalBlock>

                    <LegalBlock title="Types of cookies we use">
                        <SubBlock title="Necessary cookies">
                            <p>These cookies are essential for the website to function properly. They enable core features such as navigation, form submission and security.</p>
                            <p>These cookies do not require your consent.</p>
                        </SubBlock>
                        <SubBlock title="Analytics cookies">
                            <p>These cookies help us understand how visitors use our website so we can improve content and performance.</p>
                            <p>These cookies are only activated after you provide consent through our cookie banner.</p>
                        </SubBlock>
                        <SubBlock title="Marketing cookies">
                            <p>Marketing cookies may be used to measure the effectiveness of communications or campaigns.</p>
                            <p>These cookies are only activated after you provide consent.</p>
                        </SubBlock>
                    </LegalBlock>

                    <LegalBlock title="Legal basis for cookies (GDPR)">
                        <p>For users in the European Economic Area (EEA) and United Kingdom:</p>
                        <BulletList
                            items={[
                                "Necessary cookies are processed under our legitimate interest in operating the website",
                                "Analytics and marketing cookies are processed only with your consent",
                            ]}
                        />
                        <p>You may withdraw your consent at any time.</p>
                    </LegalBlock>

                    <LegalBlock title="Managing your consent">
                        <p>
                            You can manage your cookie preferences at any time by selecting{" "}
                            <button
                                type="button"
                                onClick={() => window.dispatchEvent(new CustomEvent("cap:open-cookie-prefs"))}
                                className="underline text-navy hover:text-teal"
                                data-testid="cookie-policy-manage-link"
                            >
                                Manage Cookies
                            </button>{" "}
                            in the website footer.
                        </p>
                        <p>You can also control or delete cookies through your browser settings.</p>
                    </LegalBlock>

                    <LegalBlock title="Third-party cookies">
                        <p>We may use trusted third-party services (such as analytics providers) that place cookies on our behalf.</p>
                        <p>These providers process data in accordance with their own privacy policies.</p>
                    </LegalBlock>

                    <LegalBlock title="Data collected via cookies">
                        <p>Cookies may collect information such as:</p>
                        <BulletList
                            items={[
                                "IP address",
                                "Browser type",
                                "Device information",
                                "Pages visited and time spent on site",
                            ]}
                        />
                        <p>This information is used in aggregated form and is not intended to identify you personally.</p>
                    </LegalBlock>

                    <LegalBlock title="International users">
                        <p>If you are located outside Australia, including in the European Economic Area (EEA), your information may be transferred to and processed in Australia or other jurisdictions.</p>
                    </LegalBlock>

                    <LegalBlock title="Changes to this policy">
                        <p>We may update this Cookie Policy from time to time. The latest version will always be available on this page.</p>
                    </LegalBlock>

                    <LegalBlock title="Contact">
                        <p>If you have any questions about this Cookie Policy or your data, contact:</p>
                        <div className="mt-3 space-y-1 text-navy">
                            <div className="font-serif text-xl">Chief AI Partners</div>
                            <div>Sanlux Pty Ltd</div>
                            <div>Sydney, Australia</div>
                            <a href="mailto:enquiries@chiefai.partners" className="underline hover:text-teal">
                                enquiries@chiefai.partners
                            </a>
                        </div>
                    </LegalBlock>
                </div>
            </section>
        </div>
    );
}

function LegalBlock({ title, children }) {
    return (
        <div data-testid={`cookie-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h2 className="font-serif text-navy text-2xl md:text-3xl leading-tight mb-4">
                {title}
            </h2>
            <div className="space-y-4 text-base md:text-lg">{children}</div>
        </div>
    );
}

function SubBlock({ title, children }) {
    return (
        <div className="pt-2">
            <h3 className="font-serif text-navy text-xl md:text-2xl leading-tight mb-2">
                {title}
            </h3>
            <div className="space-y-3 text-base md:text-lg">{children}</div>
        </div>
    );
}

function BulletList({ items }) {
    return (
        <ul className="list-disc pl-6 space-y-1.5">
            {items.map((t) => (
                <li key={t}>{t}</li>
            ))}
        </ul>
    );
}
